import { ChevronsUpDownIcon, Download, LogOutIcon, RefreshCw, Settings, UserCircle } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/primitives/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/shared/components/primitives/sidebar";

import { ColocaAvatar } from "../data-display/ColocaAvatar";

export type ColocaNavUserProps = {
  user: {
    name: string;
    email: string;
    avatar?: string;
  };
  organization?: {
    name: string;
    avatar?: string;
  };
  accountLabel: string;
  settingsLabel: string;
  logoutLabel: string;
  organizationLabel: string;
  onAccount?: () => void;
  onSettings?: () => void;
  onLogout?: () => void;
  installApp?: {
    label: string;
    onInstall: () => void;
  };
  /** A new version is waiting: always reachable here, even once its notice is dismissed. */
  updateApp?: {
    label: string;
    onUpdate: () => void;
  };
};

export function ColocaNavUser({
  user,
  organization,
  accountLabel,
  settingsLabel,
  logoutLabel,
  organizationLabel,
  onAccount,
  onSettings,
  onLogout,
  installApp,
  updateApp,
}: ColocaNavUserProps) {
  const { isMobile, setOpenMobile } = useSidebar();

  // Account and Settings navigate while DashboardLayout stays mounted, so on mobile they would
  // leave the sidebar Sheet open on top of the destination — same reason as ColocaNavMain.
  // Logout is left alone: it unmounts the whole layout, state included.
  const navigateAndCloseOnMobile = (action?: () => void) => {
    return () => {
      action?.();
      if (isMobile) setOpenMobile(false);
    };
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger render={<SidebarMenuButton size="lg" className="aria-expanded:bg-muted" />}>
            <ColocaAvatar name={user.name} alt={user.name} src={user.avatar}></ColocaAvatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{user.name}</span>
              <span className="truncate text-xs">{user.email}</span>
            </div>
            <ChevronsUpDownIcon className="ml-auto size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            {organization ? (
              <>
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                      <ColocaAvatar
                        shape="square"
                        name={organization.name}
                        alt={organization.name}
                        src={organization.avatar}
                      ></ColocaAvatar>
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-medium">{organization.name}</span>
                        <span className="truncate text-xs">{organizationLabel}</span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </>
            ) : null}
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={navigateAndCloseOnMobile(onAccount)}>
                <UserCircle />
                {accountLabel}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={navigateAndCloseOnMobile(onSettings)}>
                <Settings />
                {settingsLabel}
              </DropdownMenuItem>
              {installApp ? (
                <DropdownMenuItem onClick={installApp.onInstall}>
                  <Download />
                  {installApp.label}
                </DropdownMenuItem>
              ) : null}
              {updateApp ? (
                <DropdownMenuItem onClick={updateApp.onUpdate}>
                  <RefreshCw />
                  {updateApp.label}
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout}>
              <LogOutIcon />
              {logoutLabel}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
